import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Button,
  Center,
  Divider,
  Heading,
  HStack,
  Icon,
  IconButton,
  Spinner,
  Stack,
  Table,
  TableContainer,
  Tag,
  Tbody,
  Td,
  Text,
  Tfoot,
  Th,
  Thead,
  Tr,
  useToast,
} from '@chakra-ui/react';
import { Show } from '@pelicargo/ui';
import {
  accountTypeOptions,
  fpcTypeOptions,
  freightServiceOptions,
  lineItemTypeOptions,
  markupCategoryOptions,
} from '@pelicargo/utils';
import { useCallback, useEffect, useMemo } from 'react';
import { useFieldArray, useForm, useFormContext, useWatch } from 'react-hook-form';
import { HiPlus, HiTrash } from 'react-icons/hi2';

import { trpc, TrpcInputs, TrpcOutputs } from '../../config/trpc';
import { useAuth } from '../../context/AuthProvider';
import { AirlineInput } from '../Autocomplete/AirlineInput';
import { AirportInput } from '../Autocomplete/AirportInput';
import { SimpleCheckbox } from '../SimpleCheckbox';
import { SimpleFieldArray } from '../SimpleFieldArray';
import { SimpleForm } from '../SimpleForm';
import { SimpleInput } from '../SimpleInput';
import { SimpleNumber } from '../SimpleNumber';
import { SimpleReactSelect } from '../SimpleReactSelect';
import { SimpleSelect } from '../SimpleSelect';

type FPC = TrpcOutputs['getFPCsByAccount'][0];

type Props = {
  accountId: number;
};

const formatFPC = (fpc: FPC) => ({
  ...fpc,
  origin_airports: fpc.origin_airports.map(({ id }) => id),
  providers: fpc.providers.map(({ id }) => id),
  account_as_requestors: fpc.account_as_requestors.map(({ id }) => id),
});

export const FulfillmentPartnerConfigurationsForm = ({ accountId }: Props) => {
  const toast = useToast();
  const { data, isLoading, refetch } = trpc.getFPCsByAccount.useQuery({ accountId }, { enabled: !!accountId });
  const { mutateAsync: deleteFPC } = trpc.deleteFPC.useMutation();

  const formattedData = useMemo(() => data?.map(formatFPC) || [], [data]);

  const methods = useForm({
    defaultValues: {
      fulfillment_partner_configurations: formattedData,
    },
  });

  useEffect(() => {
    if (data) {
      methods.reset({ fulfillment_partner_configurations: formattedData });
    }
  }, [data, formattedData, methods]);

  // If it's loading, show accordion skeletons
  if (isLoading) {
    return (
      <Center>
        <Spinner />
      </Center>
    );
  }

  const handleDelete = async (index: number) => {
    const values = methods.getValues();

    const details = values?.fulfillment_partner_configurations?.[index];
    try {
      await deleteFPC({ id: details?.id });
      toast({
        title: 'Success',
        description: `Fulfillment Partner Configuration deleted`,
        status: 'success',
        duration: 9000,
        isClosable: true,
      });
    } catch (e) {
      toast({
        title: 'Error',
        description: e?.message ?? 'Unknown error',
        status: 'error',
        duration: 8000,
      });
    }

    await refetch();
  };

  return (
    <SimpleForm {...methods} submitOnBlur={false}>
      <form>
        <Accordion allowToggle>
          <SimpleFieldArray
            name="fulfillment_partner_configurations"
            addLabel="Add FPC"
            deleteConfirmationDetails={{
              btnCancel: 'Cancel',
              btnDelete: 'Delete',
              message: 'Delete fulfillment partner configuration?',
              title: 'Delete FPC?',
            }}
            onRemove={handleDelete}
            form={({ prefix }) => <SimpleFPCForm prefix={prefix as `${number}`} accountId={accountId} />}
            ignoreStyles
          />
        </Accordion>
      </form>
    </SimpleForm>
  );
};

type SimpleFPCFormProps = {
  prefix: `${number}`;
  accountId: number;
};

type FPCInput = TrpcInputs['upsertFPC'];

const SimpleFPCForm = ({ prefix, accountId }: SimpleFPCFormProps) => {
  const fpc = useWatch<TrpcOutputs['getFPCsByAccount']>({ name: prefix }) as Exclude<FPCInput, void>;
  const toast = useToast();
  const { knownTenants } = useAuth();

  const { data: allAccounts } = trpc.getAllAccounts.useQuery();

  const { mutateAsync: upsertFPC, isLoading: submitting } = trpc.upsertFPC.useMutation();

  const methods = useForm<Exclude<FPCInput, void>>({
    defaultValues: {
      markups: fpc?.markups || [],
      account_as_fulfillment_partner_id: accountId,
      // Get only specific attributes from the already existing FPC
      providers: fpc?.providers,
      id: fpc?.id,
      label: fpc?.label,
      type: fpc?.type,
      origin_airports: fpc?.origin_airports,
      account_as_requestors: fpc?.account_as_requestors,
      account_types: fpc?.account_types || [],
      trucking_providers: fpc?.trucking_providers || [],
      is_restricted: fpc?.is_restricted || false,
      tenant_id: fpc?.tenant_id,
    },
  });

  const onValid = useCallback(
    async (values: FPCInput) => {
      try {
        await upsertFPC(values);
        toast({
          title: 'Success',
          description: `Fulfillment Partner Configuration updated`,
          status: 'success',
          duration: 9000,
          isClosable: true,
        });
      } catch (error) {
        toast({
          title: 'Unable to update',
          description: error?.message,
          status: 'error',
          duration: 9000,
          isClosable: true,
        });
      }
    },
    [toast, upsertFPC],
  );

  const handleSubmit = methods.handleSubmit(onValid);

  return (
    <AccordionItem key={prefix} my="1" borderWidth="1px" borderRadius="md">
      <AccordionButton justifyContent="space-between">
        <Text>{fpc.label || '[New FPC]'}</Text>
        <Stack direction="row" alignItems="center" spacing="4">
          <Tag size="sm" variant="subtle">
            {fpc.tenant_id}
          </Tag>
          <Show if={!!fpc?.type}>
            <Tag size="sm" variant="subtle">
              {fpc.type}
            </Tag>
          </Show>
          <AccordionIcon />
        </Stack>
      </AccordionButton>
      <AccordionPanel overflowY="auto">
        <SimpleForm {...methods}>
          <Stack spacing="4">
            <SimpleCheckbox name="is_restricted" label="Restricted" />
            <SimpleSelect
              label="Tenant"
              name="tenant_id"
              defaultValue={fpc?.tenant_id}
              options={knownTenants.map((t) => ({
                label: t,
                value: t,
              }))}
            />
            <SimpleInput name="label" label="Label" placeholder="Exchange" />
            <SimpleSelect name="type" label="Type" options={fpcTypeOptions} />
            <AirportInput name="origin_airports" label="Origin Airports" placeholder="LAX, JFK, ORD" isMulti />
            <AirlineInput name="providers" label="Airlines" placeholder="AC, UA, EK" isMulti />
            <SimpleReactSelect
              name="account_as_requestors"
              label="Connected Accounts"
              options={allAccounts?.map((account) => ({
                label: account?.name,
                value: String(account?.id),
              }))}
              isSearchable
              isMulti
            />
            <SimpleReactSelect name="account_types" label="Account Types" options={accountTypeOptions} isMulti />
            <SimpleReactSelect
              name="trucking_providers"
              label="Trucking Providers"
              options={[
                {
                  label: 'Echo',
                  value: 'Echo',
                },
                {
                  label: 'ForwardAir',
                  value: 'ForwardAir',
                },
                {
                  label: 'GlobalTranz',
                  value: 'GlobalTranz',
                },
              ]}
              isMulti
            />
            <Heading size="sm">Markups</Heading>
            <Accordion allowToggle defaultIndex={0}>
              <SimpleFieldArray
                name="markups"
                addLabel="Add Markup"
                defaultValue={{
                  applicable_freight_services: [],
                }}
                form={({ prefix }) => <SimpleMarkupForm prefix={prefix as `${number}`} accountId={accountId} />}
                ignoreStyles
              />
            </Accordion>
            <Button onClick={handleSubmit} colorScheme="brand" isLoading={submitting}>
              Update
            </Button>
          </Stack>
        </SimpleForm>
      </AccordionPanel>
    </AccordionItem>
  );
};

const MarkupCost = ({ prefix }: { prefix: string }) => {
  const { unregister } = useFormContext();

  return (
    <Tr p="0">
      <Td p="0">
        <HStack spacing="0">
          <SimpleNumber
            name={`${prefix}.min`}
            px="0"
            inputAttrs={{
              borderRadius: 0,
              textAlign: 'right',
              pr: 2,
              bg: 'none',
            }}
          />{' '}
          <Text pr="2">kg</Text>
        </HStack>
      </Td>
      <Td p="0">
        <HStack spacing="0">
          <SimpleNumber
            name={`${prefix}.cost`}
            px="0"
            inputAttrs={{
              borderRadius: 0,
              textAlign: 'right',
              pr: 2,
              bg: 'none',
            }}
          />{' '}
          <Text pr="2">¢</Text>
        </HStack>
      </Td>
      <Td p="0">
        <IconButton
          size="sm"
          aria-label="Remove Row"
          colorScheme="red"
          variant="ghost"
          borderRadius="none"
          icon={<Icon as={HiTrash} fontSize={16} />}
          onClick={() => unregister(prefix)}
        />
      </Td>
    </Tr>
  );
};

const SimpleMarkupForm = ({ prefix }: SimpleFPCFormProps) => {
  const markup = useWatch({ name: prefix });
  const { append } = useFieldArray({
    name: `${prefix}.costs`,
  });

  const isLineItemTypesEmpty = !markup?.line_item_types?.length;

  return (
    <AccordionItem key={prefix} my="1" borderWidth="1px" borderRadius="md" bg="bg.canvas">
      <AccordionButton justifyContent="space-between">
        <Text>{markup.label || '[New Markup]'}</Text>
        <AccordionIcon />
      </AccordionButton>
      <AccordionPanel overflowY="auto">
        <Stack spacing="2">
          <SimpleInput name={`${prefix}.label`} label="Label" placeholder="Exchange" />
          <SimpleSelect name={`${prefix}.category`} label="Category" options={markupCategoryOptions} />
          <SimpleReactSelect
            name={`${prefix}.line_item_types`}
            label="Hidden Under"
            optionGroupOptions={lineItemTypeOptions?.map((group) => ({
              label: group.groupLabel,
              options: group.options.map((option) => ({
                label: option.label,
                value: option.value,
              })),
            }))}
            isMulti
          />
          <SimpleReactSelect
            name={`${prefix}.applicable_freight_services`}
            label="Applicable Freight Services"
            options={freightServiceOptions}
            isMulti
          />
          <Show if={isLineItemTypesEmpty}>
            <Text textStyle="bodySmallRegular" color="text.muted">
              ⚠️ Since no "Hidden Under" types are selected, this markup will be visible to customers.
            </Text>
          </Show>
          <SimpleInput name={`${prefix}.min_cost`} label="Min Cost" placeholder="min cost ($)" type="number" />
        </Stack>
        <Divider my="0.25em" />
        <TableContainer>
          <Table variant="sensible" size="sm">
            <Thead>
              <Tr>
                <Th>Min Weight</Th>
                <Th>Rate</Th>
              </Tr>
            </Thead>
            <Tbody>{markup?.costs?.map((_, i) => <MarkupCost key={i} prefix={`${prefix}.costs.${i}`} />)}</Tbody>
            <Tfoot p="0">
              <Td p="0" colSpan={3} borderBottomRadius="md">
                <Button
                  leftIcon={<HiPlus />}
                  width="full"
                  borderTopRadius="0"
                  justifyContent="flex-start"
                  fontWeight="normal"
                  onClick={() => append({})}
                >
                  Add row
                </Button>
              </Td>
            </Tfoot>
          </Table>
        </TableContainer>
      </AccordionPanel>
    </AccordionItem>
  );
};
